// useLocalStorage - https://usehooks.com/useLocalStorage/

import { useEffect, useState } from "react";
import Bugsnag, { addErrorMetaData } from "services/bugsnag";

const useLocalStorage = <T = Record<string, any>>(
  key: string,
  initialValue: T
) => {
  const getItem = (): T => {
    let item;
    try {
      // Get from local storage by key
      item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      Bugsnag.notify(
        "Error getting local storage item",
        addErrorMetaData("storage data", {
          key,
          item,
          error,
        })
      );
      return initialValue;
    }
  };

  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(getItem());

  useEffect(() => {
    if (storedValue !== getItem()) {
      try {
        // Save to local storage
        window.localStorage.setItem(key, JSON.stringify(storedValue));
      } catch (error) {
        // A more advanced implementation would handle the error case
        Bugsnag.notify(
          "Error setting local storage item",
          addErrorMetaData("storage data", {
            key,
            storedValue,
            error,
          })
        );
      }
    }
  }, [storedValue, key]);

  return [storedValue, setStoredValue] as const;
};

export default useLocalStorage;
// FIXME: Issue with quickly changing state
